import { ListFilter, X } from 'lucide-react'
import { describeDays } from '../lib/useFilterGroup'
import { hostLabel } from '../lib/hostLabels'
import type { FilterBannerProps } from '../types'

/**
 * Shown when someone arrives on a ?group= link. Without it the calendar just
 * looks half-empty and nothing on screen explains why, which is the fastest way
 * to lose a visitor who followed a link from Reddit or Facebook.
 *
 * Deliberately spare, to NOT read as machine-generated. It states only facts a
 * person would bother writing: what the filter is (the label, e.g. the exact
 * title of the thread it answers) and which facets it includes (the categories,
 * plus the day constraint as a plain word). No summary/blurb - a warm paragraph
 * describing the filter is the thing that reads as AI, and `group.blurb` is kept
 * only as internal documentation, never rendered.
 *
 * The banner IS the "a filter is on" indicator, so it isn't separately
 * dismissible: clearing the filter is what removes it.
 */
export function FilterBanner({ group, shownCount, onClear }: FilterBannerProps) {
  // Categories, then the day constraint, as one flat list of what's included.
  // The group stores raw host values; this line has to read like the drawer.
  const facets = [...group.hosts.map(hostLabel), describeDays(group.days)].filter(Boolean)

  return (
    // One wrapping flex row, reordered by breakpoint via `order`:
    //   mobile  -> label+count / includes (full width) / Show all
    //   desktop -> label+count Show all / includes (full width)
    // The includes line is always basis-full, so it sits on its own row and the
    // button never pushes it around. On mobile the button is order-last (drops
    // below everything so the text runs edge to edge); on desktop it resets to
    // flow order, landing right after "N upcoming".
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b border-amber-200 bg-amber-50 px-3 py-1.5 text-sm dark:border-amber-900/60 dark:bg-amber-950/40">
      <ListFilter className="h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400" />

      <p className="min-w-0 text-[13px] leading-snug font-medium text-amber-900 sm:text-sm dark:text-amber-100">
        {group.label}
        <span className="ml-1.5 font-normal text-amber-800/80 dark:text-amber-200/70">
          {shownCount} upcoming
        </span>
      </p>

      <button
        type="button"
        onClick={onClear}
        className="order-last flex shrink-0 items-center gap-1 rounded-md border border-amber-300 px-2 py-1 text-xs font-medium text-amber-900 transition hover:bg-amber-100 sm:order-none dark:border-amber-800 dark:text-amber-100 dark:hover:bg-amber-900/40"
      >
        <X className="h-3.5 w-3.5" />
        Show all
      </button>

      {facets.length > 0 && (
        <p className="order-1 w-full basis-full text-xs text-amber-800/80 sm:order-none dark:text-amber-200/70">
          Includes: {facets.join(', ')}
        </p>
      )}
    </div>
  )
}
